import React from 'react';
import {
  Box,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FlowBackButton } from './components/FlowBackButton';
import { StudioFlowColors } from './studioFlowTheme';

interface WebFlowLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  largeSubtitle?: boolean;
  showBack?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onBack?: () => void;
}

export const WebFlowLayout: React.FC<WebFlowLayoutProps> = ({
  children,
  title,
  subtitle,
  largeSubtitle = false,
  showBack = false,
  maxWidth = 'sm',
  onBack,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: StudioFlowColors.background,
      }}
    >
      {!isDesktop && showBack && (
        <Box sx={{ px: 2, pt: 2 }}>
          <FlowBackButton onClick={handleBack} />
        </Box>
      )}

      <Container
        maxWidth={maxWidth}
        sx={{
          py: { xs: 3, md: 6 },
          px: { xs: 3, md: 4 },
        }}
      >
        {title && (
          <Typography
            component="h1"
            sx={{
              fontFamily: StudioFlowColors.displayFontFamily,
              color: StudioFlowColors.title,
              fontWeight: 400,
              fontSize: { xs: 34, md: 44 },
              lineHeight: 1,
              mb: subtitle ? 1 : 3,
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            sx={{
              color: StudioFlowColors.subtitle,
              fontFamily: StudioFlowColors.bodyFontFamily,
              fontWeight: largeSubtitle ? 700 : 400,
              fontSize: largeSubtitle ? 22 : 15,
              lineHeight: largeSubtitle ? 1.3 : 1.5,
              letterSpacing: largeSubtitle ? 0 : '-0.15px',
              mb: 4,
              textAlign: 'center',
            }}
          >
            {subtitle}
          </Typography>
        )}
        {children}
      </Container>
    </Box>
  );
};
